import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import { tripSlice } from "../fleet/trips/tripsSlice";
import { TripsByVehicleDriverDayApi } from "../fleet/trips/tripsApi";
import {
  DataPoint,
  GetDataPointsByVehicleAndDate,
  GetLastDataPointsByCompanyAndDate,
  LastDataPointsVehicles,
  RequestTripAddresses,
} from "./geolocationApi";

export interface GeolocationState {
  dataPointsByVehicleStatus: QueryStatus;
  lastDataPointsVehiclesStatus: QueryStatus;
  lastDataPointsVehicles: LastDataPointsVehicles | null;
  requestTripAddressesStatus: QueryStatus;
  tripUuidRequestingAddresses: string | null;
}

const initialState: GeolocationState = {
  dataPointsByVehicleStatus: "idle",
  lastDataPointsVehiclesStatus: "idle",
  lastDataPointsVehicles: null,
  requestTripAddressesStatus: "idle",
  tripUuidRequestingAddresses: null,
};

export const getDataPointsByVehicleAndDateAsync = createAsyncThunk(
  "getDataPointsByVehicleAndDateAsync/call",
  async (payload: GetDataPointsByVehicleAndDate, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;

    const response = await authAxios({
      companyUuid: company?.uuid,
    }).get<DataPoint[]>(`trips/vehicles/${payload.vehicleUuid}/locations`, {
      params: {
        date: payload.date,
      },
    });

    thunkAPI.dispatch(
      geolocationSlice.actions.setDataPointsForVehicle({
        vehicleUuid: payload.vehicleUuid,
        dataPoint: response.data,
      }),
    );
  },
);

export const getLastDataPointsByCompanyAndDateAsync = createAsyncThunk(
  "getLastDataPointsByCompanyAndDateAsync/call",
  async (payload: GetLastDataPointsByCompanyAndDate) => {
    const response = await authAxios({
      companyUuid: payload.companyUuid,
    }).get<LastDataPointsVehicles>(
      `trips/company/${payload.companyUuid}/vehicles/last_location`,
      {
        params: {
          date: payload.date,
        },
      },
    );
    return response.data;
  },
);

export const requestTripAddressesAsync = createAsyncThunk(
  "requestTripAddressesAsync/call",
  async (payload: RequestTripAddresses, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) {
      throw new Error("Company not selected");
    }

    await authAxios({
      companyUuid: company.uuid,
    }).post(`trips/${payload.tripUuid}/addresses`);

    const maxTime = 20000;
    const interval = 5000;
    const startTime = Date.now();

    // Not tripsAsync: it would replace the loaded trips list and reset its status
    const poll = async (): Promise<void> => {
      const response = await authAxios({
        companyUuid: company.uuid,
      }).get<TripsByVehicleDriverDayApi[]>(
        `trips/company/${company.uuid}/vehicles`,
        { params: { from: payload.date, to: payload.date } },
      );
      const trip = response.data
        .flatMap((tripsOfADay) => tripsOfADay.trips)
        .find((tripOfADay) => tripOfADay.uuid === payload.tripUuid);

      if (trip?.departureAddress || trip?.destinationAddress) {
        thunkAPI.dispatch(
          tripSlice.actions.setTripAddresses({
            uuid: trip.uuid,
            departureAddress: trip.departureAddress,
            destinationAddress: trip.destinationAddress,
          }),
        );
        return;
      }

      if (Date.now() - startTime > maxTime) {
        throw new Error("Timeout reached");
      }

      await new Promise((resolve) => setTimeout(resolve, interval));
      return poll();
    };

    await poll();
  },
);

export const geolocationSlice = createSlice({
  name: "geolocation",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetDataPointsByVehicleStatus(state) {
      state.dataPointsByVehicleStatus = "idle";
    },
    resetLastDataPointsVehiclesStatus(state) {
      state.lastDataPointsVehiclesStatus = "idle";
    },
    resetRequestTripAddressesStatus(state) {
      state.requestTripAddressesStatus = "idle";
    },
    setDataPointsForVehicle(
      state,
      action: PayloadAction<{ vehicleUuid: string; dataPoint: DataPoint[] }>,
    ) {
      if (!state.lastDataPointsVehicles) return;
      state.lastDataPointsVehicles = state.lastDataPointsVehicles.map(
        (vehicle) =>
          vehicle.vehicleUuid === action.payload.vehicleUuid
            ? {
                ...vehicle,
                dataPoints: action.payload.dataPoint,
              }
            : vehicle,
      );
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getDataPointsByVehicleAndDateAsync.pending, (state) => {
        state.dataPointsByVehicleStatus = "processing";
      })
      .addCase(getDataPointsByVehicleAndDateAsync.fulfilled, (state) => {
        state.dataPointsByVehicleStatus = "success";
      })
      .addCase(getDataPointsByVehicleAndDateAsync.rejected, (state) => {
        state.dataPointsByVehicleStatus = "failed";
      })
      .addCase(getLastDataPointsByCompanyAndDateAsync.pending, (state) => {
        state.lastDataPointsVehiclesStatus = "processing";
      })
      .addCase(
        getLastDataPointsByCompanyAndDateAsync.fulfilled,
        (state, action) => {
          state.lastDataPointsVehicles = action.payload;
          state.lastDataPointsVehiclesStatus = "success";
        },
      )
      .addCase(getLastDataPointsByCompanyAndDateAsync.rejected, (state) => {
        state.lastDataPointsVehiclesStatus = "failed";
      })
      .addCase(requestTripAddressesAsync.pending, (state, action) => {
        state.requestTripAddressesStatus = "processing";
        state.tripUuidRequestingAddresses = action.meta.arg.tripUuid;
      })
      .addCase(requestTripAddressesAsync.fulfilled, (state) => {
        state.requestTripAddressesStatus = "success";
        state.tripUuidRequestingAddresses = null;
      })
      .addCase(requestTripAddressesAsync.rejected, (state) => {
        state.requestTripAddressesStatus = "failed";
        state.tripUuidRequestingAddresses = null;
      });
  },
});

export const selectDataPointsByVehicleStatus = (state: RootState) =>
  state.geolocation.dataPointsByVehicleStatus;
export const selectLastDataPointsVehicles = (state: RootState) =>
  state.geolocation.lastDataPointsVehicles;
export const selectLastDataPointsVehiclesStatus = (state: RootState) =>
  state.geolocation.lastDataPointsVehiclesStatus;
export const selectRequestTripAddressesStatus = (state: RootState) =>
  state.geolocation.requestTripAddressesStatus;
export const selectTripUuidRequestingAddresses = (state: RootState) =>
  state.geolocation.tripUuidRequestingAddresses;

export default geolocationSlice.reducer;
